import { useDeleteFilesMutation } from '@gen2/api/files/hooks';
import {
  FollowUpKeys,
  useDuplicateFollowUpGroupRequestMutation,
  useUpdateFollowUpRequestGroupNameMutation,
} from '@gen2/api/follow-up/hooks';
import { TInviteRequest } from '@gen2/api/invite-requests/api';
import {
  InviteRequestsKeys,
  useCreateInviteRequestMutation,
  useDeleteInviteRequestMutation,
  useDuplicateInviteGroupRequestMutation,
  useGetInviteRequestGroupsQuery,
  useInviteRequestsQuery,
  useUpdateInviteRequestGroupNameMutation,
} from '@gen2/api/invite-requests/hooks';
import { ConfirmationModal } from '@gen2/app/components/confirmation-modal/confirmation-modal';
import { useConfirmationModalStore } from '@gen2/app/components/confirmation-modal/hooks/confirmation-modal-store';
import { EditableTitle } from '@gen2/app/components/editable-title/editable-title';
import { useEditableTitle } from '@gen2/app/components/editable-title/useEditableTitle';
import { CreateRequest } from '@gen2/app/components/invite-request/create-request/create-request';
import { useFileStore } from '@gen2/app/components/invite-request/hooks/file-store';
import { useFormModalStore } from '@gen2/app/components/invite-request/hooks/modal-store';
import { InviteRequest } from '@gen2/app/components/invite-request/invite-request';
import { useOpenSharedDrawer } from '@gen2/app/components/organizing-request-modal/hooks/useOpenSharedDrawer';
import { OrganizeRequestButton } from '@gen2/app/components/organizing-request-modal/organize-request-button';
import { validateGroupName } from '@gen2/app/components/organizing-request-modal/utils';
import { useFollowUps } from '@gen2/app/invite-listing/invite-requests-details/hooks';
import { queryClient } from '@gen2/config';
import { useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'usehooks-ts';
import { REQUEST_TITLE } from '../hooks';
import { useSendInviteStore } from '../store';
import {
  Heading,
  StyledContainer,
  StyledCreateContainer,
  StyledRequests,
  StyledRequestsHead,
} from './requests.styled';
import { inviteRequestsSchema, MAX_INVITE_REQUESTS } from './schema';
import { useRequestsStore } from './store';
export type TInviteRequestsForm = {
  requests: Array<TInviteRequest & { expanded?: boolean }>;
};

export const defaultRequests: TInviteRequestsForm = {
  requests: [
    {
      id: '0',
      title: 'New Request',
      type: 'file-upload',
      description: '',
      descriptionText: '',
      meta: '',
      status: 'draft',
      order: 0,
      files: [],
      pdf_redactions: [],
      created_at: '',
      indicator: 'draft',
      expanded: false,
    },
  ],
};

export type TInviteRequestsListProps = {
  data: Array<TInviteRequest>;
  title?: string;
  hasDelete?: boolean;
  isGroup?: boolean;
  groupId?: string;
  onUpdateGroupTitle?: (groupId: string, newTitle: string) => void;
  onDuplicateGroup?: (groupId: string) => void;
  onExpandCollapseAll?: (groupId: string) => void;
};

export const InviteRequestsList = React.memo(
  ({
    data,
    title,
    hasDelete,
    isGroup = false,
    groupId,
    onUpdateGroupTitle,
    onDuplicateGroup,
    onExpandCollapseAll,
  }: TInviteRequestsListProps) => {
    const {
      mutate: deleteInviteRequestMutation,
      isLoading: isRequestDeleting,
    } = useDeleteInviteRequestMutation();
    const {
      toDeleteRequest,
      setToDeleteRequest,
      setActiveRequest,
      expandedGroups,
      toggleGroupExpansion,
    } = useRequestsStore();
    const { t } = useTranslation('sendInvite');
    const toast = useToast();
    const { file: selectedFile, setFile: setSelectedFile } = useFileStore();
    const { setIsConfirmationModalOpen, setConfirmationModalProps } =
      useConfirmationModalStore();
    const { mutate: deleteFilesMutation, isLoading: isFileDeleting } =
      useDeleteFilesMutation();
    const { fields: requests } = useFieldArray({
      name: 'requests',
      keyName: 'fieldId', // Use 'fieldId' instead of 'id' to avoid conflicts with request.id
    });

    const { getValues } = useFormContext<TInviteRequestsForm>();
    // Use useWatch to only watch the expanded fields, preventing re-renders on other field changes
    const watchedRequests = useWatch<TInviteRequestsForm>({
      name: 'requests',
    }) as TInviteRequest[];
    const expandedStates =
      watchedRequests?.map((req: any) => req?.expanded) || [];

    // Get current requests data without watching to avoid re-renders
    const getCurrentRequests = useCallback(() => {
      return getValues('requests');
    }, [getValues]);

    const siStore = useSendInviteStore();
    const isInviteReady = !!siStore.invite.id;

    const { reset } = useFormContext<TInviteRequestsForm>();

    const editableTitle = useEditableTitle({
      title,
      isGroup,
      groupId,
      onUpdateGroupTitle,
      validateGroupName,
    });

    useEffect(() => {
      // Always update form when data changes, even if empty
      if (!data) return;

      const currentRequests = getCurrentRequests();
      const currentExpansionStates =
        currentRequests?.reduce(
          (acc, req, index) => {
            if (req.expanded !== undefined) {
              acc[index] = req.expanded;
            }
            return acc;
          },
          {} as Record<number, boolean>,
        ) || {};

      const requestsWithPreservedExpansion = data.map((req, index) => ({
        ...req,
        expanded:
          currentExpansionStates[index] ?? (req as any).expanded ?? false,
      }));

      reset({ requests: requestsWithPreservedExpansion });
    }, [siStore.invite.id, data, reset, getCurrentRequests]);

    useUnmount(() => {
      // Cleanup logic here
      if (defaultRequests.requests[0]) {
        setActiveRequest(defaultRequests.requests[0]);
      }
      reset({ requests: [] });
    });

    // Handle expand/collapse all requests in the group
    const handleExpandCollapseAll = useCallback(() => {
      if (!groupId) return;

      const currentRequests = watchedRequests;
      if (!currentRequests?.length) return;

      const allRequestsExpanded = currentRequests.every(
        (req: any) => req.expanded === true,
      );

      // Simple toggle: if all are expanded, collapse all; otherwise expand all
      const newExpandedState = !allRequestsExpanded;

      // Update group expansion state
      toggleGroupExpansion(groupId);

      // Update all individual expanded states
      const updatedRequests = currentRequests.map((req: any) => ({
        ...req,
        expanded: newExpandedState,
      }));

      reset({ requests: updatedRequests });
    }, [groupId, expandedGroups, watchedRequests, reset, toggleGroupExpansion]);

    // Calculate expand all button state
    const expandAllButtonState = useMemo(
      () =>
        expandedStates.some((expanded: boolean) => expanded === false)
          ? 'expand'
          : 'collapse',
      [expandedStates],
    );

    const requestTitle = useMemo(() => {
      if (requests.length > 0) {
        return toDeleteRequest?.title ?? '';
      }

      return '';
    }, [toDeleteRequest, requests]);

    // Memoize request components
    const memoizedRequests = useMemo(() => {
      const currentRequests = watchedRequests;

      if (!currentRequests || currentRequests.length === 0) return null;

      return currentRequests.map((request: TInviteRequest, index: number) => {
        return (
          <InviteRequest
            key={request?.id || `request-${index}`}
            id={index}
            hasDelete={hasDelete}
            isDeleteDisabled={isRequestDeleting}
            isGroupExpanded={groupId ? expandedGroups.has(groupId) : false}
          />
        );
      });
    }, [
      requests,
      hasDelete,
      isRequestDeleting,
      groupId,
      expandedGroups,
      watchedRequests,
    ]);

    const onDelete = useCallback(() => {
      if (toDeleteRequest) {
        deleteInviteRequestMutation(
          {
            inviteId: siStore.invite.id,
            requestIds: [toDeleteRequest.id],
          },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequests],
              });

              await queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequestGroups],
              });

              await queryClient.invalidateQueries({
                queryKey: [FollowUpKeys.getFollowUps],
              });

              toast.show({
                text: t('request.delete.success'),
                variant: 'success',
              });

              return null;
            },
            onError: () => {
              toast.show({
                text: t('request.delete.error'),
                variant: 'error',
              });
            },
            onSettled: () => {
              setToDeleteRequest(undefined);
              setIsConfirmationModalOpen(false);
            },
          },
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      deleteInviteRequestMutation,
      toDeleteRequest,
      setIsConfirmationModalOpen,
      toast,
    ]);

    useEffect(() => {
      if (!requestTitle) return;

      setConfirmationModalProps({
        onClose: () => setIsConfirmationModalOpen(false),
        onSubmit: onDelete,
        isLoading: isRequestDeleting,
        title: t('request.delete.title'),
        submitLabel: t('request.delete.buttonLabel'),
        submitColor: 'danger',
        message: (() => {
          if (siStore.isEditSendInvite) {
            return t('editSendInvite.request.delete.message');
          }
          if (requestTitle) {
            return t('request.delete.message', { requestName: requestTitle });
          }
          return t('request.delete.message_no_subject');
        })(),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isRequestDeleting,
      onDelete,
      requestTitle,
      setConfirmationModalProps,
      setIsConfirmationModalOpen,
    ]);

    const { closeCreateFormModal } = useFormModalStore();

    const handleFileDelete = useCallback(() => {
      if (selectedFile) {
        deleteFilesMutation(
          {
            type: 'request',
            typeId: selectedFile.request.id,
            files: [{ id: selectedFile.id }],
          },
          {
            onSuccess: () => {
              siStore.setBannerMessage({
                severity: 'success',
                message: t('request.fileUpload.message.deleted') ?? '',
              });

              queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequests],
              });

              queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequestGroups],
              });

              queryClient.invalidateQueries({
                queryKey: [FollowUpKeys.getFollowUps],
              });

              toast.show({
                text: t('file.delete.success'),
                variant: 'success',
              });

              closeCreateFormModal();
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (err: any) => {
              siStore.setBannerMessage({
                severity: 'error',
                message: err.data.message,
              });

              toast.show({
                text: t('file.delete.error'),
                variant: 'error',
              });
            },
            onSettled: () => {
              setSelectedFile(undefined);
              setIsConfirmationModalOpen(false);
            },
          },
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFile, siStore, toast]);

    useEffect(() => {
      if (!selectedFile) return;

      const isDeleteFileFromFormType =
        selectedFile.request.type === 'live-form';

      setConfirmationModalProps({
        onClose: () => setIsConfirmationModalOpen(false),
        onSubmit: handleFileDelete,
        isLoading: isFileDeleting,
        title: isDeleteFileFromFormType
          ? t('editSendInvite.file.delete.title')
          : t('file.delete.title'),
        submitLabel: t('file.delete.buttonLabel'),
        submitColor: 'danger',
        message:
          isDeleteFileFromFormType && siStore.isEditSendInvite
            ? t('editSendInvite.file.delete.message')
            : t('file.delete.message', {
                fileName: selectedFile.original_name,
              }),
      });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFileDeleting, selectedFile]);

    return (
      <StyledContainer
        $opaque={!isInviteReady}
        ref={(node: HTMLDivElement | null) =>
          node &&
          (!isInviteReady
            ? node.setAttribute('inert', '')
            : node.removeAttribute('inert'))
        }
      >
        <StyledRequests $isGroup={isGroup}>
          <StyledRequestsHead $isGroup={isGroup}>
            <EditableTitle
              title={editableTitle.editTitle}
              isEditing={editableTitle.isEditingTitle}
              onEdit={editableTitle.handleSwitchToEditMode}
              onDuplicateGroup={() => onDuplicateGroup?.(groupId || '')}
              onExpandCollapseAll={handleExpandCollapseAll}
              onChange={editableTitle.handleTitleChange}
              onSave={editableTitle.handleTitleSave}
              error={editableTitle.editTitleError}
              helperText={editableTitle.editTitleError}
              isGroup={isGroup}
              expandAllButtonState={
                !expandedStates.some((expanded: boolean) => expanded === false)
                  ? 'collapse'
                  : 'expand'
              }
              inputProps={{ 'data-testid': 'edit-group-title-input' }}
            />
          </StyledRequestsHead>
          <div>{memoizedRequests}</div>
        </StyledRequests>
      </StyledContainer>
    );
  },
);

export type TFollowUpRequestsListProps = {
  title: string;
  requests: Array<TInviteRequest>;
  requestsGroup: Array<TInviteRequestGroup>;
  disabled?: boolean;
  followUpId: string;
};

const FollowUpRequestsListComponent: React.FC<TFollowUpRequestsListProps> = ({
  title,
  requests,
  requestsGroup,
  disabled,
  followUpId,
}) => {
  const siStore = useSendInviteStore();
  const { inviteId } = useParams<{ inviteId: string }>();
  const toast = useToast();
  const { mutateAsync: updateFollowUpRequestGroupNameMutation } =
    useUpdateFollowUpRequestGroupNameMutation();

  const { mutate: duplicateFollowUpGroupRequestMutation } =
    useDuplicateFollowUpGroupRequestMutation();

  const initialRequests = useMemo(
    () => requests?.filter((request) => !request.request_group_id) || [],
    [requests],
  );

  const openSharedDrawer = useOpenSharedDrawer();

  const handleOpenDrawer = () => {
    openSharedDrawer({
      type: 'followup',
      items: initialRequests,
      inviteId: siStore.invite.id || inviteId,
      followUpId: followUpId,
      itemsToGroup: requestsGroup,
    });
  };

  // Add this function for followup group title update
  const handleUpdateGroupTitle = async (groupId: string, newTitle: string) => {
    await updateFollowUpRequestGroupNameMutation(
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        inviteId: (siStore.invite.id || inviteId)!,
        followUpId: followUpId,
        requestGroupId: groupId,
        data: { name: newTitle },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [FollowUpKeys.getFollowUps],
          });
        },
      },
    );
  };

  // duplicate group request for follow up
  const handleDuplicateGroupForFollowUp = (groupId: string) => {
    duplicateFollowUpGroupRequestMutation(
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        inviteId: (siStore.invite.id || inviteId)!,
        followUpId: followUpId,
        requestGroupId: groupId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [FollowUpKeys.getFollowUps],
          });
        },
        onError: (e: unknown) => {
          const error = e as AxiosResponse;
          if (error.status === 422) {
            toast.show({
              text: error.data?.message,
              variant: 'error',
            });
          }
        },
      },
    );
  };

  const handleExpandCollapseAll = (groupId: string) => {
    // This function is called from the FollowUpRequestsList component
    // but the actual logic is handled in the InviteRequestsList component
  };

  return (
    <>
      <Permissions allow={PermissionsKeys.REORDER_FOLLOW_UP}>
        <OrganizeRequestButton
          onClick={handleOpenDrawer}
          mt={48}
          disabled={disabled}
          isContextReady={Boolean(requests.length || requestsGroup.length)}
        />
      </Permissions>
      <Heading>{title}</Heading>
      {requestsGroup &&
        requestsGroup.length > 0 &&
        requestsGroup.map((group: TInviteRequestGroup) => (
          <div key={group.id}>
            <InviteRequestsListWithForm
              key={
                group.id +
                '-' +
                (group.requests?.map((r) => r.id).join(',') ?? '')
              }
              title={group.name}
              data={group.requests || []}
              hasDelete
              isGroup={true}
              groupId={group.id}
              onUpdateGroupTitle={handleUpdateGroupTitle}
              onDuplicateGroup={handleDuplicateGroupForFollowUp}
              onExpandCollapseAll={handleExpandCollapseAll}
            />
          </div>
        ))}
      {initialRequests.length > 0 && (
        <InviteRequestsListWithForm
          key={'initfollowup-' + initialRequests.map((r) => r.id).join(',')}
          data={initialRequests}
          hasDelete
        />
      )}
    </>
  );
};

export const FollowUpRequestsList = React.memo(FollowUpRequestsListComponent);

export const FollowUpRequests = React.memo(() => {
  const siStore = useSendInviteStore();
  const { inviteId } = useParams<{ inviteId: string }>();
  const { followUps, isLoading, isRefetching } = useFollowUps(
    (siStore.invite.id || inviteId) ?? '',
  );

  if (!followUps.sent.length) {
    return null;
  }

  return (
    <>
      {followUps.sent.map((followUp) => (
        <FollowUpRequestsList
          key={followUp.id}
          title={followUp.name}
          requests={followUp.requests}
          requestsGroup={followUp.request_groups}
          disabled={isLoading || isRefetching}
          followUpId={followUp.id}
        />
      ))}
    </>
  );
});

export type TInviteRequestGroup = {
  id: string;
  name: string;
  order: number;
  requests: Array<TInviteRequest>;
};

// New wrapper component for independent form context
const InviteRequestsListWithForm = ({
  data,
  title,
  hasDelete,
  isGroup,
  groupId,
  onUpdateGroupTitle,
  onDuplicateGroup,
  onExpandCollapseAll,
}: TInviteRequestsListProps) => {
  const methods = useForm<TInviteRequestsForm>({
    defaultValues: { requests: data },
    resolver: yupResolver(inviteRequestsSchema),
  });

  return (
    <FormProvider {...methods}>
      <InviteRequestsList
        data={data}
        title={title}
        hasDelete={hasDelete}
        isGroup={isGroup}
        groupId={groupId}
        onUpdateGroupTitle={onUpdateGroupTitle}
        onDuplicateGroup={onDuplicateGroup}
        onExpandCollapseAll={onExpandCollapseAll}
      />
    </FormProvider>
  );
};

export const InviteRequests = () => {
  const { t } = useTranslation('sendInvite');
  const siStore = useSendInviteStore();
  const { mutate: createInviteRequestMutation, isLoading: isCreating } =
    useCreateInviteRequestMutation();
  const toast = useToast();
  const params = useParams<{ id: string }>();
  // for new invite draft & updating invite both
  const isInviteIdReady = !!siStore.contextInviteIdForNewInvite || params.id;
  const { data, isLoading, isRefetching } = useInviteRequestsQuery(
    {
      inviteId: siStore.invite.id,
      'sort[order]': 'asc',
      'sort[column]': 'order',
      per_page: 100,
    },
    {
      enabled: !!siStore.invite.id,
    },
  );

  const {
    data: inviteRequestGroups,
    isLoading: isLoadingInviteRequestGroups,
    isRefetching: isRefetchingInviteRequestGroups,
  } = useGetInviteRequestGroupsQuery(siStore.invite.id, {
    enabled: !!siStore.invite.id,
  });

  const {
    mutateAsync: updateInviteRequestGroupNameMutation,
    isLoading: isUpdatingGroupName,
  } = useUpdateInviteRequestGroupNameMutation();

  const { mutateAsync: duplicateGroupRequestMutation } =
    useDuplicateInviteGroupRequestMutation();

  const { followUps } = useFollowUps(siStore.invite.id);

  const initialRequests = useMemo(
    () => data?.filter((request) => !request.request_group_id) || [],
    [data],
  );

  const initialRequestGroups = useMemo(
    () => inviteRequestGroups || [],
    [inviteRequestGroups],
  );

  // Calculate total requests count (grouped + ungrouped) for delete button logic
  const totalRequestsCount = useMemo(() => {
    return data?.length || 0;
  }, [data]);

  const openSharedDrawer = useOpenSharedDrawer();

  const handleOpenDrawer = () => {
    openSharedDrawer({
      type: 'invite',
      items: initialRequests,
      inviteId: siStore.invite.id,
      itemsToGroup: initialRequestGroups,
    });
  };

  const onCreate = () => {
    if ((data?.length || 0) >= MAX_INVITE_REQUESTS) {
      siStore.setUpgradeModal({
        isOpen: true,
        title: t('request.upgrade.title') ?? '',
        description: t('request.upgrade.description') ?? '',
      });

      return;
    }

    createInviteRequestMutation(
      {
        inviteId: siStore.invite.id,
        title: REQUEST_TITLE,
        type: 'file-upload',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [InviteRequestsKeys.getInviteRequests],
          });

          return null;
        },
        onError: () => {
          toast.show({
            text: t('request.create.error'),
            variant: 'error',
          });
        },
      },
    );
  };

  const handleUpdateGroupTitle = async (groupId: string, newTitle: string) => {
    await updateInviteRequestGroupNameMutation({
      inviteId: siStore.invite.id,
      requestGroupId: groupId,
      data: { name: newTitle },
    });
  };

  const handleDuplicateInviteGroup = async (groupId: string) => {
    try {
      await duplicateGroupRequestMutation({
        inviteId: siStore.invite.id,
        requestGroupId: groupId,
      });
    } catch (e) {
      const error = e as AxiosResponse;
      if (error.status === 422) {
        toast.show({
          text: error.data?.message,
          variant: 'error',
        });
      } else {
        toast.show({
          text: 'Error duplicating invite group',
          variant: 'error',
        });
      }
    }
  };

  return (
    <>
      <Permissions allow={PermissionsKeys.REORDER_REQUEST_INVITE}>
        <OrganizeRequestButton
          onClick={handleOpenDrawer}
          mt={48}
          disabled={
            isCreating ||
            isLoading ||
            isRefetching ||
            isRefetchingInviteRequestGroups ||
            isLoadingInviteRequestGroups ||
            isUpdatingGroupName
          }
          isContextReady={Boolean(isInviteIdReady)}
        />
      </Permissions>
      {!!followUps.sent.length && <Heading>Initial Requests</Heading>}
      {initialRequestGroups &&
        initialRequestGroups.length > 0 &&
        initialRequestGroups.map((group: TInviteRequestGroup) => (
          <div key={group.id}>
            <InviteRequestsListWithForm
              key={
                group.id +
                '-' +
                (group.requests?.map((r) => r.id).join(',') ?? '')
              }
              title={group.name}
              data={group.requests || []}
              hasDelete={(totalRequestsCount || 0) > 1}
              isGroup={true}
              groupId={group.id}
              onUpdateGroupTitle={handleUpdateGroupTitle}
              onDuplicateGroup={handleDuplicateInviteGroup}
            />
          </div>
        ))}
      {initialRequests.length > 0 && (
        <InviteRequestsListWithForm
          key={'initial-' + initialRequests.map((r) => r.id).join(',')}
          title={''}
          data={initialRequests}
          hasDelete={totalRequestsCount > 1}
        />
      )}
      <StyledCreateContainer>
        <CreateRequest onClick={onCreate} disabled={isCreating} />
      </StyledCreateContainer>
    </>
  );
};

export const SendInviteRequests = React.memo(() => {
  return (
    <>
      <FollowUpRequests />
      <InviteRequests />
      <ConfirmationModal />
    </>
  );
});
